import { MotionConfig, motion, useReducedMotion } from 'framer-motion';
import { AlertTriangle, ArrowLeft, ArrowRight, FileType2, Loader2, Sparkles } from 'lucide-react';
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import FlowStepsVertical from './flow-step-verticle';
import {
  COMPANY_SUB_ORDER,
  CompanySubKind,
  EMAIL_RE,
  FillVariable,
  FLOW_STEPS,
  ResolvedSignatory,
  SUB_ORDER,
  SubKind,
} from '../utils';
import { StepperCompact } from '@/components/shared/Stepper';
import { Button } from '@/components/ui-kit/button';
import RenderPersonForm from './render-person-form';
import RenderCompanyForm from './render-company-form';
import { RenderTextForm } from './render-text-form';
import { ConfirmDetailsDialog } from './confirm-details-dialog';
import { useNavigate } from 'react-router-dom';

interface DocxFillFormProps {
  title: string;
  slug: string;
  variables: FillVariable[];
  values: Record<string, string>;
  setValues: Dispatch<SetStateAction<Record<string, string>>>;
  setFieldErrors: Dispatch<SetStateAction<Set<string>>>;
  setFormError: Dispatch<SetStateAction<string | null>>;
  formError: string | null;
  fieldErrors: Set<string>;
  isPreviewMode: boolean;
  description: string | null;
  previewLoading: boolean;
  /** Called at generate time with the signatories resolved from the filled form. */
  onResolveSignatories?: (signatories: ResolvedSignatory[]) => void;
  /** Called after the user confirms the consent dialog — starts the real generation pipeline. */
  onGenerate?: (signatories: ResolvedSignatory[]) => void;
}

export default function DocxFillForm({
  title,
  variables,
  values,
  setFieldErrors,
  setFormError,
  setValues,
  formError,
  isPreviewMode,
  previewLoading,
  // description,
  fieldErrors,
  onResolveSignatories,
  onGenerate,
}: DocxFillFormProps) {
  const prefersReducedMotion = useReducedMotion();
  const navigate = useNavigate();

  // Consent dialog state: the signatories resolved from the form, held until the
  // user confirms the "this cannot be undone" prompt.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingSignatories, setPendingSignatories] = useState<ResolvedSignatory[]>([]);

  // Move focus back to the form heading when returning from the preview (the whole view
  // swaps, so without this focus falls to <body>). Skipped on the initial mount.
  const formHeadingRef = useRef<HTMLHeadingElement>(null);
  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (hasMountedRef.current) {
      formHeadingRef.current?.focus();
    }
    hasMountedRef.current = true;
  }, []);

  // Signatory emails, keyed by personKey (= parent variable name). These are NOT
  // document tokens — they're captured here for SELISE Signature (send flow lands with
  // DOCX→PDF support) and never merged into the .docx.
  const [emails, setEmails] = useState<Record<string, string>>({});

  // Build the variable tree: top-level rows (scalars + person parents) and a parentId →
  // children map. Person sub-fields render grouped inside their parent's card. Fields that
  // mirror another (`valueCopyFrom`) are NOT rendered — they inherit the source's value.
  const { topLevel, childrenByParent } = useMemo(() => {
    const childrenByParent = new Map<string, FillVariable[]>();
    const topLevel: FillVariable[] = [];
    for (const v of variables) {
      if (v.valueCopyFrom) continue; // mirrored field — no input, filled from its source
      if (v.parentId) {
        const arr = childrenByParent.get(v.parentId) ?? [];
        arr.push(v);
        childrenByParent.set(v.parentId, arr);
      } else {
        topLevel.push(v);
      }
    }
    // Person children order by SUB_ORDER, company children by COMPANY_SUB_ORDER — a group's
    // children are always all of one kind, so the first child decides which to use.
    for (const arr of childrenByParent.values()) {
      if (arr.some((c) => c.companySubKind)) {
        arr.sort(
          (a, b) =>
            COMPANY_SUB_ORDER[a.companySubKind ?? 'name'] -
            COMPANY_SUB_ORDER[b.companySubKind ?? 'name']
        );
      } else {
        arr.sort((a, b) => SUB_ORDER[a.subKind ?? 'name'] - SUB_ORDER[b.subKind ?? 'name']);
      }
    }
    return { topLevel, childrenByParent };
  }, [variables]);

  // Leaves = everything that maps to a real token AND is filled by hand (scalars + person
  // sub-fields). A person PARENT has no token; a mirrored field is filled from its source;
  // a signature sub-field is a location anchor, not a fill-in — none count toward
  // progress/required.
  const leafVariables = useMemo(
    () =>
      variables.filter(
        (v) =>
          v.type !== 'person' &&
          v.type !== 'company' &&
          !v.valueCopyFrom &&
          v.subKind !== 'signature'
      ),
    [variables]
  );

  // Company children sharing a companyRef are filled once and kept in sync. Map each
  // company child name → all sibling company child names (incl. itself).
  const companyGroupOf = useMemo(() => {
    const refOfParent = new Map<string, string>();
    for (const p of topLevel) {
      if (p.type === 'person') refOfParent.set(p.id, p.companyRef ?? p.name);
    }
    const byRef = new Map<string, string[]>();
    for (const v of variables) {
      if (v.subKind === 'company' && v.parentId) {
        const ref = refOfParent.get(v.parentId) ?? v.parentId;
        const arr = byRef.get(ref) ?? [];
        arr.push(v.name);
        byRef.set(ref, arr);
      }
    }
    const map = new Map<string, string[]>();
    for (const names of byRef.values()) for (const n of names) map.set(n, names);
    return map;
  }, [topLevel, variables]);

  // Signatory persons (a parent flagged isSignatory). Each requires a signing email —
  // mandatory to generate, even though the email itself need not appear in the document.
  const signatories = useMemo(
    () => topLevel.filter((v) => v.type === 'person' && v.isSignatory),
    [topLevel]
  );

  // A signatory's signing email comes from its rendered `email` sub-field when it has one
  // (so we never ask twice); otherwise from a dedicated, non-rendered signing-email input.
  const emailChildName = useCallback(
    (parent: FillVariable): string | null =>
      (childrenByParent.get(parent.id) ?? []).find((c) => c.subKind === 'email')?.name ?? null,
    [childrenByParent]
  );
  const dedicatedSignatories = useMemo(
    () => signatories.filter((p) => !emailChildName(p)),
    [signatories, emailChildName]
  );

  // Progress/required cover document leaves plus the dedicated signing-email inputs (an
  // email captured via a rendered sub-field is already counted as a leaf).
  const totalInputs = leafVariables.length + dedicatedSignatories.length;
  const requiredCount = useMemo(() => {
    const requiredLeaves = new Set(leafVariables.filter((v) => v.required).map((v) => v.name));
    // A signatory's rendered email child is mandatory-by-signing even if not flagged
    // required — count it if it isn't already a required leaf.
    const extraEmailChildren = signatories.filter((p) => {
      const child = emailChildName(p);
      return child && !requiredLeaves.has(child);
    }).length;
    return requiredLeaves.size + extraEmailChildren + dedicatedSignatories.length;
  }, [leafVariables, signatories, emailChildName, dedicatedSignatories]);
  const filledCount = useMemo(
    () =>
      leafVariables.filter((v) => String(values[v.name] ?? '').trim() !== '').length +
      dedicatedSignatories.filter((p) => String(emails[p.name] ?? '').trim() !== '').length,
    [leafVariables, values, dedicatedSignatories, emails]
  );
  const progressPct = totalInputs ? Math.round((filledCount / totalInputs) * 100) : 0;

  const setValue = useCallback((name: string, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setFormError(null);
    setFieldErrors((prev) => {
      if (!prev.has(name)) return prev;
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A shared company is filled once: write the value to every linked company child so
  // each token in the document resolves, and the server need do no special fan-out.
  const setCompanyValue = useCallback(
    (name: string, value: string) => {
      const group = companyGroupOf.get(name) ?? [name];
      setValues((prev) => {
        const next = { ...prev };
        for (const n of group) next[n] = value;
        return next;
      });
      setFormError(null);
      setFieldErrors((prev) => {
        if (!group.some((n) => prev.has(n))) return prev;
        const next = new Set(prev);
        for (const n of group) next.delete(n);
        return next;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyGroupOf]
  );

  const setEmail = useCallback((personKey: string, value: string) => {
    setEmails((prev) => ({ ...prev, [personKey]: value }));
    setFormError(null);
    // Email errors are flagged in fieldErrors under the person's key.
    setFieldErrors((prev) => {
      if (!prev.has(personKey)) return prev;
      const next = new Set(prev);
      next.delete(personKey);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fill a person's sub-fields from a source (a picked contact or the author's default
  // signer). Each value maps to the child whose subKind matches; a shared company fans
  // out to every linked token; a signatory's email lands in its dedicated input when the
  // person has no rendered email sub-field. Blank source values are skipped (never clear).
  const applyPersonValues = useCallback(
    (parent: FillVariable, src: Partial<Record<SubKind, string | null | undefined>>) => {
      const kids = childrenByParent.get(parent.id) ?? [];
      for (const child of kids) {
        if (!child.subKind) continue;
        const raw = src[child.subKind];
        if (raw == null || String(raw).trim() === '') continue;
        if (child.subKind === 'company') setCompanyValue(child.name, String(raw));
        else setValue(child.name, String(raw));
      }
      const hasEmailChild = kids.some((c) => c.subKind === 'email');
      if (!hasEmailChild && parent.isSignatory && src.email && String(src.email).trim()) {
        setEmail(parent.name, String(src.email));
      }
    },
    [childrenByParent, setValue, setCompanyValue, setEmail]
  );

  // Fill a Company entity's sub-fields from a picked company — the company counterpart of
  // `applyPersonValues`. Blank source values are skipped so a sparse company record never
  // clears what's already entered.
  const applyCompanyValues = useCallback(
    (parent: FillVariable, src: Partial<Record<CompanySubKind, string | null | undefined>>) => {
      const kids = childrenByParent.get(parent.id) ?? [];
      for (const child of kids) {
        if (!child.companySubKind) continue;
        const raw = src[child.companySubKind];
        if (raw == null || String(raw).trim() === '') continue;
        setValue(child.name, String(raw));
      }
    },
    [childrenByParent, setValue]
  );

  // Pre-fill people from the template author's default signer (a frozen snapshot). Runs
  // once on mount; the user can override anything afterward.
  const didPrefillRef = useRef(false);
  useEffect(() => {
    if (didPrefillRef.current) return;
    didPrefillRef.current = true;
    for (const p of topLevel) {
      if (p.type === 'person' && p.defaultSigner) {
        applyPersonValues(p, p.defaultSigner);
      }
    }
  }, [topLevel, applyPersonValues]);

  const handleGenerate = useCallback(() => {
    // Required document fields.
    const missingDocs = leafVariables
      .filter((v) => v.required && !String(values[v.name] ?? '').trim())
      .map((v) => v.name);
    // Signatory emails are MANDATORY (and must be valid) — they're how the document is
    // sent for signature. The value comes from the rendered email sub-field when present,
    // else the dedicated signing-email input. Flag whichever input the admin must fix.
    const badEmails = signatories
      .filter((p) => {
        const child = emailChildName(p);
        const val = child ? values[child] : emails[p.name];
        return !EMAIL_RE.test(String(val ?? '').trim());
      })
      .map((p) => emailChildName(p) ?? p.name);

    if (missingDocs.length > 0 || badEmails.length > 0) {
      // Email errors are flagged under the person key (dedicated input) or the email
      // sub-field's name; doc errors under the field name.
      setFieldErrors(new Set([...missingDocs, ...badEmails]));
      setFormError(
        badEmails.length > 0 && missingDocs.length === 0
          ? 'Add a valid signing email for each signatory below.'
          : 'Please complete the required fields highlighted below.'
      );
      // A rendered email sub-field is a normal leaf input (id `fill-…`); a dedicated
      // signing email lives under the person key (id `email-…`).
      const firstBad = missingDocs[0] ?? badEmails[0];
      const dedicatedKeys = new Set(dedicatedSignatories.map((p) => p.name));
      const isDedicatedEmail = missingDocs.length === 0 && dedicatedKeys.has(firstBad);
      const first = document.getElementById(
        isDedicatedEmail ? `email-${firstBad}` : `fill-${firstBad}`
      );
      first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      first?.focus({ preventScroll: true });
      return;
    }
    setFieldErrors(new Set());
    setFormError(null);

    // Resolve the signatory list for the preview panel: each person parent flagged as a
    // signatory, with its sub-field values (name/role/company) and signing email (from the
    // rendered email sub-field when present, else the dedicated signing-email input).
    const resolved: ResolvedSignatory[] = signatories.map((p) => {
      const kids = childrenByParent.get(p.id) ?? [];
      const kindVal = (k: SubKind) => {
        const child = kids.find((c) => c.subKind === k);
        return child ? String(values[child.name] ?? '').trim() : '';
      };
      const emailChild = emailChildName(p);
      const email = emailChild
        ? String(values[emailChild] ?? '').trim()
        : String(emails[p.name] ?? '').trim();
      // The signature sub-field (if the template marks one) carries the text-context
      // anchor used to place this signatory's signature in the converted PDF.
      const signatureChild = kids.find((c) => c.subKind === 'signature');
      return {
        key: p.name,
        name: kindVal('name') || p.label,
        role: kindVal('role'),
        company: kindVal('company'),
        email,
        signingOrder: p.signingOrder ?? null,
        signatureAnchor: signatureChild?.sourceSpan ?? null,
      };
    });
    onResolveSignatories?.(resolved);

    // Hold the resolved signatories and ask the user to confirm before the
    // irreversible generation pipeline runs.
    setPendingSignatories(resolved);
    setConfirmOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    leafVariables,
    signatories,
    childrenByParent,
    dedicatedSignatories,
    emailChildName,
    emails,
    values,
    onResolveSignatories,
  ]);

  const handleConfirmGenerate = useCallback(() => {
    setConfirmOpen(false);
    if (typeof window !== 'undefined') window.scrollTo({ top: 0 });
    onGenerate?.(pendingSignatories);
  }, [onGenerate, pendingSignatories]);

  const onBack = () => {
    void navigate(-1);
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="min-h-screen bg-[hsl(var(--bg))]">
        <div className="mx-auto max-w-6xl 2xl:max-w-7xl px-4 py-6 lg:py-10">
          <div className="flex gap-8 lg:gap-12">
            {/* Left: flow + progress (desktop) */}
            <aside className="hidden lg:block w-64 2xl:w-80 shrink-0">
              <div className="sticky top-20 space-y-6">
                <div className="flex items-center gap-3">
                  <Button
                    onClick={onBack}
                    variant={'ghost'}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-selise-blue-500/10"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-[hsl(var(--fg))]">{title}</p>
                    <p className="text-xs text-globe-grey">Word template</p>
                  </div>
                </div>

                {/* 2-step flow indicator (matches the smart-flow stepper). */}
                <FlowStepsVertical steps={FLOW_STEPS} currentStep={0} />

                {totalInputs > 0 && (
                  <div className="rounded-xl border border-border bg-card p-4">
                    <div className="mb-2 flex items-center justify-between text-xs">
                      <span className="font-medium text-[hsl(var(--fg))]">Progress</span>
                      <span className="text-globe-grey">
                        {filledCount}/{totalInputs}
                      </span>
                    </div>
                    <div
                      className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
                      role="progressbar"
                      aria-valuenow={progressPct}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label="Fields completed"
                    >
                      <motion.div
                        className="h-full rounded-full bg-selise-blue-500"
                        initial={false}
                        animate={{ width: `${progressPct}%` }}
                        transition={{
                          duration: prefersReducedMotion ? 0 : 0.3,
                          ease: 'easeOut',
                        }}
                      />
                    </div>
                    {requiredCount > 0 && (
                      <p className="mt-2 text-xs text-globe-grey">
                        {requiredCount} required field
                        {requiredCount === 1 ? '' : 's'}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </aside>

            {/* Right: form */}
            <main className="min-w-0 flex-1">
              {/* Mobile flow indicator */}
              <div className="lg:hidden mb-6">
                <StepperCompact steps={FLOW_STEPS} currentStep={0} />
                <p className="mt-1 text-center text-xs text-globe-grey">
                  Step 1 of 2 — Fill in details
                </p>
              </div>

              {isPreviewMode && (
                <div className="mb-6 flex items-center gap-2 rounded-xl border border-selise-blue/20 bg-selise-blue/5 px-4 py-3 text-sm text-globe-grey">
                  <span className="rounded-full bg-selise-blue/15 px-2 py-0.5 text-xs font-semibold text-selise-blue">
                    Preview
                  </span>
                  This template is a draft, not yet published to your organization.
                </div>
              )}

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="rounded-2xl border border-border bg-background p-5 shadow-sm sm:p-8 "
              >
                {/* Header */}
                <div className="mb-6 flex items-center gap-4 border-b border-border pb-6">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
                    <FileType2 className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h1
                      ref={formHeadingRef}
                      tabIndex={-1}
                      className="text-base md:text-xl font-semibold text-[hsl(var(--fg))] font-heading outline-none sm:text-2xl"
                    >
                      Fill in the details
                    </h1>
                    <p className="mt-0.5 text-sm truncate text-brand-muted">
                      {title || 'Complete the fields below to generate your Word document.'}
                    </p>
                  </div>
                </div>

                {topLevel.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-border bg-muted/30 px-6 py-10 text-center">
                    <p className="text-sm text-globe-grey">
                      This template has no fields to fill in. Generate to preview and download it.
                    </p>
                  </div>
                ) : (
                  <motion.div
                    className="space-y-5 sm:space-y-8"
                    variants={{
                      hidden: { opacity: 0 },
                      show: {
                        opacity: 1,
                        transition: { staggerChildren: 0.06 },
                      },
                    }}
                    initial="hidden"
                    animate="show"
                  >
                    {topLevel.map((v) => (
                      <motion.div
                        key={v.parentId ? v.id : v.name}
                        variants={{
                          hidden: { opacity: 0, y: 12 },
                          show: { opacity: 1, y: 0 },
                        }}
                      >
                        {v.type === 'person' ? (
                          <RenderPersonForm
                            applyPersonValues={applyPersonValues}
                            parent={v}
                            values={values}
                            childrenByParent={childrenByParent}
                            emails={emails}
                            fieldErrors={fieldErrors}
                            setEmail={setEmail}
                            setCompanyValue={setCompanyValue}
                            setValue={setValue}
                          />
                        ) : v.type === 'company' ? (
                          <RenderCompanyForm
                            applyCompanyValues={applyCompanyValues}
                            parent={v}
                            values={values}
                            childrenByParent={childrenByParent}
                            fieldErrors={fieldErrors}
                            setCompanyValue={setCompanyValue}
                            setValue={setValue}
                          />
                        ) : (
                          <RenderTextForm
                            value={values[v.name]}
                            setCompanyValue={setCompanyValue}
                            setValue={setValue}
                            invalid={fieldErrors.has(v.name)}
                            v={v}
                          />
                        )}
                      </motion.div>
                    ))}
                  </motion.div>
                )}

                {formError && (
                  <div
                    role="alert"
                    aria-live="assertive"
                    className="mt-6 flex items-center gap-2 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  >
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    {formError}
                  </div>
                )}

                {/* Footer */}
                <div className="mt-8 flex flex-col-reverse items-stretch justify-between gap-3 border-t border-border pt-6 sm:flex-row sm:items-center">
                  <p className="text-xs text-globe-grey">
                    Your details merge into the Word template — the formatting stays exactly as the
                    original.
                  </p>
                  <Button
                    onClick={handleGenerate}
                    disabled={previewLoading}
                    size="lg"
                    className="group gap-2 px-5 py-3 transition-all duration-300 hover:shadow-lg sm:px-8 sm:py-4"
                  >
                    {previewLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Sparkles className="h-5 w-5 transition-transform group-hover:scale-110" />
                    )}
                    <span className="text-sm sm:text-base">Generate document</span>
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                </div>
              </motion.div>
            </main>
          </div>
        </div>

        {/* Consent dialog — irreversible generation gate. */}
        <ConfirmDetailsDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          leafVariables={leafVariables}
          values={values}
          signatories={pendingSignatories}
          onConfirm={handleConfirmGenerate}
        />
      </div>
    </MotionConfig>
  );
}
